import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { effectiveSource } from './model'
import type {
  FieldSource,
  FieldValue,
  SigningFact,
  SigningConfiguration,
  SigningField,
} from './model'
import { SourceEditor } from './SourceEditor'
import { SigningRow } from './SigningRow'

export function SigningFieldRow({
  id,
  field,
  configuration,
  value,
  facts,
  managesFields,
  previewing,
  selected,
  issue,
  onSelect,
  onUpdateField,
  onUpdateConfiguration,
}: {
  id: string
  field: SigningField
  configuration?: SigningConfiguration
  value?: FieldValue
  facts: SigningFact[]
  managesFields: boolean
  previewing: boolean
  selected: boolean
  issue?: string
  onSelect: (toggle?: boolean) => void
  onUpdateField: (field: SigningField) => void
  onUpdateConfiguration: (configuration: SigningConfiguration) => void
}) {
  const source = effectiveSource(field, configuration)
  const problem = issue
  const editable = managesFields
    ? field.ownership === 'template'
    : field.ownership === 'organization' && Boolean(configuration)
  const updateSource = (next: FieldSource) => {
    if (managesFields) onUpdateField({ ...field, source: next })
    else if (configuration)
      onUpdateConfiguration({
        ...configuration,
        mappings: { ...configuration.mappings, [field.name]: next },
      })
  }

  return (
    <SigningRow
      id={id}
      name={field.name}
      label={field.label}
      required={field.required}
      locked={field.ownership === 'template'}
      selected={selected}
      previewing={previewing}
      onSelect={onSelect}
      onRequiredChange={
        managesFields
          ? (required) => onUpdateField({ ...field, required })
          : undefined
      }
      onManagedChange={
        managesFields
          ? (managed) =>
              onUpdateField({
                ...field,
                ownership: managed ? 'template' : 'organization',
                source: managed
                  ? (source ?? {
                      kind: 'literal',
                      value: field.type === 'checkbox' ? false : '',
                    })
                  : undefined,
              })
          : undefined
      }>
      {previewing && (!managesFields || field.ownership === 'template') ? (
        <div className="grid min-w-0 grid-cols-[88px_minmax(0,1fr)] items-center gap-1 text-sm">
          <span className="px-2 text-xs text-muted-foreground">
            {source ? (source.kind === 'fact' ? 'Fact' : 'Entered') : ''}
          </span>
          {field.type === 'checkbox' && typeof value === 'boolean' ? (
            <div className="flex h-8 items-center px-2">
              <Checkbox
                aria-label={`${field.label} preview`}
                aria-invalid={Boolean(problem)}
                aria-describedby={problem ? `${id}-issue` : undefined}
                checked={value === true}
                disabled
                className="disabled:opacity-100"
              />
            </div>
          ) : (
            <output
              aria-label={`${field.label} preview`}
              aria-invalid={Boolean(problem)}
              aria-describedby={problem ? `${id}-issue` : undefined}
              className={cn(
                'flex h-8 min-w-0 items-center rounded-md border border-transparent px-2',
                problem && 'border-destructive'
              )}>
              <span
                className="truncate"
                title={value === undefined ? undefined : String(value)}>
                {value === undefined || value === '' ? '—' : String(value)}
              </span>
            </output>
          )}
        </div>
      ) : (
        <SourceEditor
          field={field}
          catalogue={facts}
          source={source}
          managed={managesFields}
          disabled={!editable}
          issue={problem}
          issueId={`${id}-issue`}
          onChange={updateSource}
        />
      )}
      {problem && (
        <span id={`${id}-issue`} className="sr-only">
          {problem}
        </span>
      )}
    </SigningRow>
  )
}
